import { useEffect, useRef, useState } from 'react';
import NotificationItem from './NotificationItem';
import { fetchListNotification, GET_MARK_ALL_AS_READ } from '../../../apis/notification.api';
import NotificationIcon from '../../../assets/notification-icon.svg';
import SettingNotifyIcon from '../../../assets/setting-notify-icon.svg';
import { useClickOutside } from '../../../hooks/useOutsideClick';
import Cookies from 'js-cookie';
import NotificationSettingsModal from './notification-modal.component';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@chakra-ui/react';
import { groupNotificationsByTime } from './utils/notification-grouping';

function NotificationComponent() {
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPage: 0,
    unreadCount: 0
  });
  const [listNotification, setListNotification] = useState([]);
  const [isOpenPopup, setIsOpenPopup] = useState(false);
  const [isOpenDetail, setIsOpenDetail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [modal, setModal] = useState('');
  const [active, setActive] = useState(0);
  const [initialized, setInitialized] = useState(false);
  const navigate = useNavigate();

  const dropdownRef = useRef(null);
  const detailRef = useRef(null);
  const listRef = useRef(null);

  useClickOutside(dropdownRef, () => {
    setIsOpenPopup(false);
  });

  useClickOutside(detailRef, () => {
    setIsOpenDetail(false);
  });

  const loadMoreData = () => {
    if (!loading && pagination.page < pagination.totalPage) {
      setLoading(true);
      setPagination((prevPagination) => ({
        ...prevPagination,
        page: prevPagination.page + 1
      }));
    }
  };

  const handleToggleNotifyDropdown = () => {
    if (!initialized) {
      setTabLoading(true);
      setInitialized(true);
    }
    setIsOpenPopup((prevOpen) => !prevOpen);
  };

  const handleScroll = () => {
    if (listRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      if (scrollHeight - scrollTop <= clientHeight + 100) {
        loadMoreData();
      }
    }
  };

  const markAllAsRead = async () => {
    const response = await fetch(GET_MARK_ALL_AS_READ, {
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        'Content-Type': 'application/json'
      }
    });
    const result = await response.json();
    if (result.success) {
      setListNotification((prevList) =>
        prevList.map((item) => ({
          ...item,
          markAsRead: true
        }))
      );
      setPagination({
        ...pagination,
        unreadCount: 0
      });
    }
    setIsOpenDetail(false);
  };

  const handleCloseModal = () => {
    setModal('');
  };

  useEffect(() => {
    const listElement = listRef.current;
    if (listElement) {
      listElement.addEventListener('scroll', handleScroll);
      return () => {
        listElement.removeEventListener('scroll', handleScroll);
      };
    }
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const response = await fetchListNotification({
          page: 1,
          limit: pagination.limit,
          readStatus: 'ALL'
        });

        if (response?.data?.length > 0) {
          setListNotification(response.data);
          setPagination((prevPagination) => ({
            ...prevPagination,
            totalPage: response.totalPage,
            unreadCount: response.unreadCount,
            total: response.count
          }));
        }
      } catch (error) {
        console.error('Error loading initial notification data:', error);
      } finally {
        setInitialized(true);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchListNotification({
          page: pagination.page,
          limit: pagination.limit,
          readStatus: active === 0 ? 'ALL' : 'UNREAD'
        });

        if (response?.data?.length > 0) {
          setListNotification((prevList) =>
            pagination.page === 1 ? response.data : [...prevList, ...response.data]
          );
          setPagination((prevPagination) => ({
            ...prevPagination,
            totalPage: response.totalPage,
            unreadCount: response.unreadCount,
            total: response.count
          }));
        } else if (pagination.page === 1) {
          setListNotification([]);
        }
      } catch (error) {
        console.error('Error loading more data:', error);
      } finally {
        setLoading(false);
        setTabLoading(false);
      }
    };

    if (loading || tabLoading) {
      fetchData();
    }
  }, [pagination.page, loading, tabLoading, active]);

  const handleTabChange = (index) => {
    if (tabLoading || active === index) return;
    setActive(index);
    setPagination({
      ...pagination,
      page: 1
    });
    setTabLoading(true);
  };

  return (
    <div className="flex gap-4 justify-center items-center" ref={dropdownRef}>
      <div
        className="relative bg-[#F2F3F7] rounded-full w-9 h-9 md:h-10 md:w-10 flex items-center justify-center cursor-pointer hover:opacity-80"
        onClick={(e) => {
          e.stopPropagation();
          handleToggleNotifyDropdown();
        }}
      >
        <img className="w-5 h-5 2x:w-6 2xl:h-6" src={NotificationIcon} alt="notify" />
        {pagination.unreadCount > 0 && (
          <div className="absolute bg-red-600 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center font-bold right-1 top-1">
            {pagination.unreadCount > 99 ? '99+' : pagination.unreadCount}
          </div>
        )}
      </div>
      {isOpenPopup && (
        <div
          ref={listRef}
          className="absolute top-14 right-0 md:right-12 w-[360px] md:w-[490px] p-3 md:p-5 shadow-md border border-gray-200 rounded-lg overflow-y-auto max-h-[500px] bg-white"
        >
          <div className="p-2 flex justify-between items-center mb-2">
            <span className="font-extrabold text-lg text-growth-green-500">Notifications</span>
            <div ref={detailRef}>
              <img
                src={SettingNotifyIcon}
                alt="settings"
                onClick={(e) => {
                  e.stopPropagation();
                  if (listNotification.length > 0) {
                    setIsOpenDetail((prev) => !prev);
                  } else {
                    setModal('notification-settings');
                    setIsOpenPopup(false);
                  }
                }}
                className="cursor-pointer hover:opacity-80 transition-all duration-200"
              />
              {isOpenDetail && (
                <div className="absolute right-0 top-16 w-42 p-2 rounded-lg shadow-md border border-gray-200 z-50 bg-white flex flex-col gap-1">
                  <div
                    className="py-[6px] px-[14px] hover:bg-[#A6D38D] rounded-md transition-all cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/notifications');
                      setIsOpenPopup(false);
                      setIsOpenDetail(false);
                    }}
                  >
                    <span className="text-sm font-[900]">View all notifications</span>
                  </div>
                  <div
                    className="py-[6px] px-[14px] hover:bg-[#A6D38D] rounded-md transition-all cursor-pointer"
                    onClick={() => {
                      setModal('notification-settings');
                      setIsOpenPopup(false);
                    }}
                  >
                    <span className="text-sm font-[900]">Settings</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between px-2 mb-4">
            <div className="flex items-center h-[35px] w-fit bg-[#F0EDEC] rounded-[8px] p-[2px] shadow-[inset_0_2px_2px_rgba(0,0,0,0.15)]">
              {[
                { label: 'All', count: pagination.total },
                { label: 'Unread', count: pagination.unreadCount }
              ].map((tab, index) => (
                <button
                  key={tab.label}
                  className={`flex items-center gap-2 h-[30px] px-3 rounded-[7px] text-sm font-[800] ${active === index ? 'bg-white shadow-[0_2px_1px_rgba(0,0,0,0.15)] text-growth-green-900' : 'text-[#4B3A32]'} ${tabLoading && active === index ? 'opacity-70' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTabChange(index);
                  }}
                >
                  <span>{tab.label}</span>
                  <span
                    className={`min-w-[28px] h-[26px] rounded-[6px] flex items-center justify-center text-[10px] ${index === 1 ? 'bg-[#4BA54A] text-white' : 'bg-[#E7E1DE] text-[#4B3A32]'}`}
                  >
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
            <button
              className="h-[30px] px-3 py-[2px] rounded-[7px] bg-white text-sm text-[#6B6B6B] border-[1px] border-[#CCCCCC] flex items-center gap-2 hover:opacity-80"
              onClick={(e) => {
                e.stopPropagation();
                markAllAsRead();
              }}
            >
              <svg
                className="w-4 h-4 hidden md:block"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M3.5 8.5L6.6 11.5L12.5 4.5"
                  stroke="#6B6B6B"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="whitespace-nowrap">Mark all read</span>
            </button>
          </div>

          {tabLoading ? (
            <div className="w-full flex justify-center py-8">
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="green.500"
                size="md"
              />
            </div>
          ) : (
            <>
              {listNotification.length > 0 ? (
                <>
                  {groupNotificationsByTime(listNotification).map((group, groupIndex) => (
                    <div key={groupIndex} className="mb-4">
                      <div className="px-2 py-2 mb-2">
                        <span className="text-sm font-[900] text-gray-500">{group.label}</span>
                      </div>
                      {group.notifications.map((elm, index) => (
                        <NotificationItem
                          key={`${groupIndex}-${index}`}
                          elm={elm}
                          index={index}
                          setListNotification={setListNotification}
                          setPagination={setPagination}
                        />
                      ))}
                    </div>
                  ))}
                </>
              ) : (
                <div className="w-full text-center py-6 text-gray-500">No notifications found</div>
              )}
              {loading && (
                <div className="w-full flex justify-center py-4">
                  <Spinner
                    thickness="3px"
                    speed="0.65s"
                    emptyColor="gray.200"
                    color="green.500"
                    size="sm"
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
      {modal === 'notification-settings' ? (
        <NotificationSettingsModal
          isOpen
          title="Notifications Settings"
          onClose={handleCloseModal}
          titleBtn="Done"
          maxW="735px"
          maxH="736px"
        />
      ) : null}
    </div>
  );
}

export default NotificationComponent;
